import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconPhotoPlus, IconSparkles, IconTrash } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type DragEvent } from 'react';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { Kartica } from '../postavitev/Podatki';
import { useSeja } from '../seja/useSeja';
import { barve } from '../tema';
import { SlikaVozila } from './SlikaVozila';
import { GumbUrejanjaAi, UrejanjeSlikeAi } from './UrejanjeSlikeAi';

type Slika = Shema['SlikaVozila'];

/**
 * Slike vozila za javno ponudbo in oglas: dodajanje (več naenkrat, s telefona tudi s kamero, na računalniku tudi s
 * povlekom), vrstni red s puščicama (prva je naslovna), brisanje in urejanje z umetno inteligenco (✨, ko je na voljo;
 * sprejeta urejena slika zamenja izvirnik in je označena z »AI«). Pomanjša jih strežnik. Seznam je lokalen, da
 * nalaganje slik ne prekine urejanja vozila.
 */
export function SlikeVozila({ voziloId, zacetne }: { voziloId: number; zacetne: Slika[] }) {
  const odjemalec = useQueryClient();
  const [slike, setSlike] = useState<Slika[]>(zacetne);
  const [nalagam, setNalagam] = useState<{ trenutna: number; vseh: number } | null>(null);
  const [napake, setNapake] = useState<string[]>([]);
  const [brisem, setBrisem] = useState<Slika | null>(null);
  const [dela, setDela] = useState(false);
  const [povlek, setPovlek] = useState(false);
  const [urejam, setUrejam] = useState<Slika | null>(null);
  const izbira = useRef<HTMLInputElement>(null);
  const seja = useSeja();
  const ai = seja.status === 'prijavljen' && !!seja.instanca.umetnaInteligenca;

  const dodaj = async (datoteke: File[]) => {
    if (datoteke.length === 0) {
      return;
    }
    setNapake([]);
    const nove: string[] = [];
    for (let i = 0; i < datoteke.length; i++) {
      setNalagam({ trenutna: i + 1, vseh: datoteke.length });
      const f = datoteke[i]!;
      try {
        const telo = new FormData();
        telo.append('slika', f);
        const s = await podatki(
          api.POST('/api/vozila/{id}/slike', {
            params: { path: { id: voziloId } },
            // multipart: FormData gre, kot je (brskalnik doda mejo); tip v shemi je opis polja
            body: telo as unknown as { slika: string },
          }),
        );
        setSlike((z) => [...z, s]);
      } catch (e) {
        const n = e instanceof NapakaApi && e.polja.slika ? e.polja.slika : sporocilo(e);
        nove.push(`${f.name}: ${n}`);
      }
    }
    setNalagam(null);
    setNapake(nove);
    await odjemalec.invalidateQueries({ queryKey: ['vozila'] });
  };

  const premakni = async (i: number, smer: number) => {
    const j = i + smer;
    if (j < 0 || j >= slike.length) {
      return;
    }
    const red = [...slike];
    [red[i], red[j]] = [red[j]!, red[i]!];
    setSlike(red);
    try {
      setSlike(
        await podatki(
          api.PUT('/api/vozila/{id}/slike/vrstni-red', {
            params: { path: { id: voziloId } },
            body: red.map((s) => s.id!),
          }),
        ),
      );
    } catch (e) {
      setNapake([sporocilo(e)]);
      setSlike(slike);
    }
  };

  const izbrisi = async () => {
    if (!brisem) {
      return;
    }
    setDela(true);
    try {
      await izvedi(
        api.DELETE('/api/vozila/{id}/slike/{slikaId}', { params: { path: { id: voziloId, slikaId: brisem.id! } } }),
      );
      setSlike((z) => z.filter((s) => s.id !== brisem.id));
      setBrisem(null);
    } catch (e) {
      setNapake([sporocilo(e)]);
      setBrisem(null);
    } finally {
      setDela(false);
    }
  };

  const spusti = (e: DragEvent) => {
    e.preventDefault();
    setPovlek(false);
    void dodaj([...e.dataTransfer.files].filter((f) => f.type.startsWith('image/')));
  };

  return (
    <Kartica
      naslov="Slike"
      dejanja={
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPhotoPlus size={16} />}
          onClick={() => izbira.current?.click()}
          loading={!!nalagam}
        >
          Dodaj slike
        </Button>
      }
    >
      <input
        ref={izbira}
        type="file"
        accept="image/jpeg,image/png,image/*"
        multiple
        hidden
        aria-label="Izberi slike"
        onChange={(e) => {
          void dodaj([...(e.currentTarget.files ?? [])]);
          e.currentTarget.value = '';
        }}
      />
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setPovlek(true);
        }}
        onDragLeave={() => setPovlek(false)}
        onDrop={spusti}
        style={{ outline: povlek ? `2px dashed ${barve.primarna}` : undefined, borderRadius: 10 }}
      >
        {slike.length === 0 ? (
          <UnstyledButton
            onClick={() => izbira.current?.click()}
            w="100%"
            p="lg"
            style={{ border: `2px dashed ${barve.obroba}`, borderRadius: 10, textAlign: 'center' }}
          >
            <Stack align="center" gap={4}>
              <IconPhotoPlus size={32} color={barve.besediloSekundarno} />
              <Text size="sm" fw={600}>
                Dodajte slike vozila
              </Text>
              <Text size="xs" c="dimmed">
                Prva slika je naslovna. Lahko jih tudi povlečete sem.
              </Text>
            </Stack>
          </UnstyledButton>
        ) : (
          <SimpleGrid cols={{ base: 2, xs: 3 }} spacing="xs">
            {slike.map((s, i) => (
              <Box key={s.id} pos="relative">
                <SlikaVozila voziloId={voziloId} slikaId={s.id} sirina="100%" />
                {i === 0 && (
                  <Badge size="xs" variant="filled" style={{ position: 'absolute', left: 6, top: 6 }}>
                    Naslovna
                  </Badge>
                )}
                {s.urejenaAi && (
                  <Badge
                    size="xs"
                    variant="filled"
                    color="dark"
                    leftSection={<IconSparkles size={10} />}
                    style={{ position: 'absolute', right: 6, top: 6, opacity: 0.85 }}
                  >
                    AI
                  </Badge>
                )}
                <Group gap={2} justify="space-between" mt={2} wrap="nowrap">
                  <Group gap={2} wrap="nowrap">
                    <ActionIcon
                      variant="subtle"
                      size="md"
                      disabled={i === 0}
                      onClick={() => void premakni(i, -1)}
                      aria-label="Premakni naprej"
                    >
                      <IconChevronLeft size={18} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      size="md"
                      disabled={i === slike.length - 1}
                      onClick={() => void premakni(i, 1)}
                      aria-label="Premakni nazaj"
                    >
                      <IconChevronRight size={18} />
                    </ActionIcon>
                  </Group>
                  <Group gap={2} wrap="nowrap">
                    {ai && <GumbUrejanjaAi onClick={() => setUrejam(s)} />}
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="md"
                      onClick={() => setBrisem(s)}
                      aria-label="Izbriši sliko"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>
      {nalagam && (
        <Text size="sm" c="dimmed" mt="xs">
          Nalagam sliko {nalagam.trenutna} od {nalagam.vseh} …
        </Text>
      )}
      {napake.length > 0 && (
        <Alert color="red" variant="light" mt="sm" role="alert">
          {napake.map((n) => (
            <div key={n}>{n}</div>
          ))}
        </Alert>
      )}
      <UrejanjeSlikeAi
        voziloId={voziloId}
        slika={urejam}
        zapri={() => setUrejam(null)}
        zamenjana={(stara, nova) => {
          setSlike((z) => z.map((x) => (x.id === stara.id ? nova : x)));
          void odjemalec.invalidateQueries({ queryKey: ['vozila'] });
        }}
      />
      <Modal opened={!!brisem} onClose={() => !dela && setBrisem(null)} title="Izbriši sliko" radius="lg">
        <Stack gap="sm">
          <Text size="sm">Slika bo izbrisana tudi z javne strani vozila.</Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => setBrisem(null)} disabled={dela}>
              Prekliči
            </Button>
            <Button color="red" onClick={() => void izbrisi()} loading={dela}>
              Izbriši
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Kartica>
  );
}
